import { useState, useEffect } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building,
  MapPin,
  Phone,
  Check,
  AlertCircle,
  ArrowRight,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'

const CATEGORY_MAP = {
  Automotive: [
    "Auto Accessories",
    "Auto Dealers – New",
    "Auto Dealers – Used",
    "Detail & Carwash",
    "Gas Stations",
    "Motorcycle Sales & Repair",
    "Rental & Leasing",
    "Service, Repair & Parts",
    "Towing",
  ],

  "Business Support & Supplies": [
    "Consultants",
    "Employment Agency",
    "Marketing & Communications",
    "Office Supplies",
    "Printing & Publishing",
  ],

  "Computers & Electronics": [
    "Computer Programming & Support",
    "Consumer Electronics & Accessories",
  ],

  "Construction & Contractors": [
    "Architects, Landscape Architects",
    "Engineers & Surveyors",
    "Blasting & Demolition",
    "Building Materials & Supplies",
    "Construction Companies",
    "Electricians",
    "Environmental Assessments",
    "Inspectors",
    "Plaster & Concrete",
    "Plumbers",
    "Roofers",
  ],

  Education: [
    "Adult & Continuing Education",
    "Early Childhood Education",
    "Educational Resources",
    "Other Educational",
  ],

  Entertainment: [
    "Artists, Writers",
    "Event Planners & Supplies",
    "Golf Courses",
    "Movies",
    "Productions",
  ],

  "Food & Dining": [
    "Desserts, Catering & Supplies",
    "Fast Food & Carry Out",
    "Grocery, Beverage & Tobacco",
    "Restaurants",
  ],

  "Health & Medicine": [
    "Acupuncture",
    "Assisted Living & Home Health Care",
    "Audiologist",
    "Chiropractic",
    "Clinics & Medical Centers",
    "Dental",
    "Diet & Nutrition",
    "Laboratory, Imaging & Diagnostic",
    "Massage Therapy",
    "Mental Health",
    "Nurse",
    "Optical",
    "Pharmacy, Drug & Vitamin Stores",
    "Physical Therapy",
    "Physicians & Assistants",
    "Podiatry",
    "Social Worker",
    "Animal Hospital",
    "Veterinary & Animal Surgeons",
  ],

  "Home & Garden": [
    "Antiques & Collectibles",
    "Cleaning",
    "Crafts, Hobbies & Sports",
    "Flower Shops",
    "Home Furnishings",
    "Home Goods",
    "Home Improvements & Repairs",
    "Landscape & Lawn Service",
    "Pest Control",
    "Pool Supplies & Service",
    "Security System & Services",
  ],

  "Legal & Financial": [
    "Accountants",
    "Attorneys",
    "Financial Institutions",
    "Financial Services",
    "Insurance",
    "Other Legal",
  ],

  "Manufacturing, Wholesale, Distribution": [
    "Distribution, Import/Export",
    "Manufacturing",
    "Wholesale",
  ],

  "Merchants (Retail)": [
    "Cards & Gifts",
    "Clothing & Accessories",
    "Department Stores, Sporting Goods",
    "General",
    "Jewelry",
    "Shoes",
  ],

  Miscellaneous: [
    "Civic Groups",
    "Funeral Service Providers & Cemeteries",
    "Miscellaneous",
    "Utility Companies",
  ],

  "Personal Care & Services": [
    "Animal Care & Supplies",
    "Barber & Beauty Salons",
    "Beauty Supplies",
    "Dry Cleaners & Laundromats",
    "Exercise & Fitness",
    "Massage & Body Works",
    "Nail Salons",
    "Shoe Repairs",
    "Tailors",
  ],

  "Real Estate": [
    "Agencies & Brokerage",
    "Agents & Brokers",
    "Apartment & Home Rental",
    "Mortgage Broker & Lender",
    "Property Management",
    "Title Company",
  ],

  "Travel & Transportation": [
    "Hotel, Motel & Extended Stay",
    "Moving & Storage",
    "Packaging & Shipping",
    "Transportation",
    "Travel & Tourism",
  ],

  
  "Daily Needs & Food Shops": [
    "Grocery Store / Kirana Store",
    "Supermarket / Hypermarket",
    "Vegetable & Fruit Shop",
    "Bakery",
    "Sweet Shop",
  ],

  "Clothing & Fashion Shops": [
    "Men’s Wear Shop",
    "Women’s Wear Shop",
    "Boutique",
    "Textile Shop",
    "Footwear Shop",
  ],

    "Beauty & Personal Care": [
    "Cosmetics Shop",
    "Salon / Spa",
    "Beauty Parlour",
    "Perfume Store",
    "Skincare Store",
  ],

    "Health & Medical Shops": [
    "Pharmacy / Medical Store",
    "Ayurvedic Store",
    "Health & Wellness Store",
    "Optical Shop (Spectacles)",
  ],
  
    "Electronics & Mobile Shops": [
    "Mobile Store",
    "Mobile Accessories Shop",
    "Electronics Store",
    "Computer / Laptop Shop",
  ],

   "Home & Furniture Shops": [
    "Furniture Store",
    "Home Decor Shop",
    "Kitchenware Store",
    "Electrical Shop",
  ],

   "Specialty Shops": [
    "Book Store",
    "Gift Shop",
    "Toy Shop"  ,
    "Pet Shop",
    "Sports Shop",
  ],

  "Automobile Shops": [
    "Bike Showroom",
    "Car Showroom",
    "Spare Parts Shop",
    "Tyre Shop",
    "Service Center",
  ],

  "Large Retail Formats": [
    "Department Store",
    "Discount Store",
    "Mall / Shopping Complex",
    "Warehouse Store",
  ],

  
  "Food & Beverage Shops": [
    "Restaurant",
    "Café",
    "Fast Food Center",
    "Bakery Outlet",
    "Ice Cream Shop",
  ],

   "Agriculture & Rural Shops": [
    "Seed Store",
    "Fertilizer Shop",
    "Fast Pesticide Shop",
    "Animal Feed Store",
  ],

  "Content Creators & Influencers": [
  "YouTuber",
  "Instagram Influencer",
  "Content Creator",
  "Vlogger",
  "Podcaster",
  "Blogger",
  "Digital Creator",
  "Short-form Video Creator (Reels/Shorts)",
  "Live Streamer"
],

"Working Professionals": [
  "Software Engineer / Developer",
  "Doctor",
  "Lawyer",
  "Chartered Accountant",
  "Teacher / Professor",
  "Corporate Employee",
  "HR Professional",
  "Marketing Professional",
  "Sales Executive",
  "Banking Professional"
],

"Freelancers & Solopreneurs": [
  "Freelance Designer (UI/UX, Graphic)",
  "Freelance Developer",
  "Freelance Writer / Copywriter",
  "Video Editor",
  "Photographer",
  "Digital Marketer",
  "SEO Specialist",
  "Social Media Manager",
  "Consultant",
  "Virtual Assistant"
],




}


const RegisterPage = () => {
  const { register, isAuthenticated, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    categories: [],
    subcategories: [],
    location: '',
    orderId: ''
  })

  useEffect(() => {
    if (location.state?.dbOrderId) {
        setFormData(prev => ({ ...prev, orderId: location.state.dbOrderId }))
    }
  }, [location])
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (isAuthenticated) {
    // If we have payment success state, pass it along to dashboard
    if (location.state?.paymentSuccess) {
        return <Navigate to="/app/dashboard" state={location.state} replace />
    }
    return <Navigate to="/app/dashboard" replace />
  }

  const validateStep1 = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const toggleCategory = (category) => {
    setFormData(prev => {
      const newCategories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
      
      // Filter out subcategories that don't belong to remaining categories
      const validSubcategories = prev.subcategories.filter(sub => {
        // Find which category this sub belongs to
        const parentCategory = Object.keys(CATEGORY_MAP).find(cat => 
          CATEGORY_MAP[cat].includes(sub)
        )
        return newCategories.includes(parentCategory)
      })

      return {
        ...prev,
        categories: newCategories,
        subcategories: validSubcategories
      }
    })
  }

  const toggleSubcategory = (subcategory) => {
    setFormData(prev => {
      const newSubcategories = prev.subcategories.includes(subcategory)
        ? prev.subcategories.filter(s => s !== subcategory)
        : [...prev.subcategories, subcategory]
      return { ...prev, subcategories: newSubcategories }
    })
  }


  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateStep1()) {
      setStep(1)
      return
    }

    setIsSubmitting(true)
    const result = await register(formData)

    if (result.success) {
      toast.success('🎉 Welcome to FiindIt! Your account has been created successfully!')
    } else {
      toast.error(result.error)
    }

    setIsSubmitting(false)
  }

  return (
    <>
      <Helmet>
        <title>Join FiindIt 🚀 - Create Your Digital Profile</title>
        <meta name="description" content="Join FiindIt and create your smart digital profile. Connect instantly, share effortlessly, and grow your network." />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center px-4 py-8 transition-colors duration-300">
        <div className="max-w-md w-full">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">F</span>
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">FiindIt</span>
            </Link>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {step === 1 
                ? (location.state?.paymentSuccess ? '🎉 Payment Successful!' : 'Create Account') 
                : '🙋‍♂️ Tell us about yourself'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {step === 1
                ? (location.state?.paymentSuccess ? 'Create your account to activate your subscription' : 'Start your journey with FiindIt')
                : 'Help us personalize your digital profile'
              }
            </p>
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-4 mb-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}>
                {step > 1 ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}>
                2
              </div>
            </div>
            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>Account Details</span>
              <span>Profile Info</span>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-black"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${errors.name
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}
                        placeholder="Enter your full name"
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {formData.name && !errors.name && (
                        <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {errors.name && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1 flex items-center space-x-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.name}</span>
                      </motion.p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${errors.email
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}
                        placeholder="Enter your email address"
                      />
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {formData.email && !errors.email && /\S+@\S+\.\S+/.test(formData.email) && (
                        <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1 flex items-center space-x-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.email}</span>
                      </motion.p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${errors.password
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}
                        placeholder="Create a strong password"
                      />
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1 flex items-center space-x-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.password}</span>
                      </motion.p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${errors.confirmPassword
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}
                        placeholder="Confirm your password"
                      />
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {formData.confirmPassword && formData.password === formData.confirmPassword && (
                        <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {errors.confirmPassword && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-1 flex items-center space-x-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span>{errors.confirmPassword}</span>
                      </motion.p>
                    )}
                  </div>

                  <motion.button
                    type="button"
                    onClick={handleNextStep}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-blue-400 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-600 hover:to-accent-600 transition-all flex items-center justify-center space-x-2"
                  >
                    <span>Continue</span>
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 transition-all"
                        placeholder="Your phone number"
                      />
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Company Field */}
                  {/* Categories Multi-Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Categories
                    </label>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.categories.map(cat => (
                          <span key={cat} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                            {cat}
                            <button
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              className="ml-1.5 inline-flex items-center justify-center text-primary-400 hover:text-primary-600 focus:outline-none"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        {formData.categories.length === 0 && (
                          <span className="text-gray-400 text-sm py-1">Select categories below</span>
                        )}
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-1 custom-scrollbar pt-2 border-t border-gray-200 dark:border-gray-700">
                        {Object.keys(CATEGORY_MAP).map((category) => (
                          <div
                            key={category}
                            onClick={() => toggleCategory(category)}
                            className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                              formData.categories.includes(category)
                                ? 'bg-primary-50 dark:bg-primary-900/20'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 ${
                              formData.categories.includes(category)
                                ? 'bg-primary-500 border-primary-500'
                                : 'border-gray-300 dark:border-gray-500'
                            }`}>
                              {formData.categories.includes(category) && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{category}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Subcategories Multi-Select */}
                  {formData.categories.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-2"
                    >
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Subcategories
                      </label>
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.subcategories.map(sub => (
                            <span key={sub} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-300">
                              {sub}
                              <button
                                type="button"
                                onClick={() => toggleSubcategory(sub)}
                                className="ml-1.5 inline-flex items-center justify-center text-accent-400 hover:text-accent-600 focus:outline-none"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          {formData.subcategories.length === 0 && (
                            <span className="text-gray-400 text-sm py-1">Select subcategories below</span>
                          )}
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1 custom-scrollbar pt-2 border-t border-gray-200 dark:border-gray-700">
                          {formData.categories.flatMap(cat => 
                            CATEGORY_MAP[cat]?.map(sub => ({ sub, cat }))
                          ).map(({ sub, cat }) => (
                            <div
                              key={`${cat}-${sub}`}
                              onClick={() => toggleSubcategory(sub)}
                              className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                                formData.subcategories.includes(sub)
                                  ? 'bg-accent-50 dark:bg-accent-900/20'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 ${
                                formData.subcategories.includes(sub)
                                  ? 'bg-accent-500 border-accent-500'
                                  : 'border-gray-300 dark:border-gray-500'
                              }`}>
                                {formData.subcategories.includes(sub) && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{sub}</span>
                              <span className="ml-auto text-xs text-gray-400">{cat}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}



   {/* Location Field */}
                
<div> <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"> Location </label> <div className="relative"> <input type="text" name="location" value={formData.location} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 transition-all" placeholder="Your city Name Only" /> <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" /> </div> </div>
<div className="flex space-x-4 pt-4">
<motion.button
  type="button"
  onClick={() => setStep(1)}
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
  className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
>
                      Back
                    </motion.button>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:from-primary-600 hover:to-accent-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <span>Create Account</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </form>

            {/* Login Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
            >
              <p className="text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/app/login"
                  className="text-primary-500 hover:text-primary-600 font-medium transition-colors"
                >
                  Sign in here 🔐
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default RegisterPage 